import type { TypeId } from '@unipile/db';
import type { ProviderAuthenticationCheckpointInput, ProviderAuthenticationIntentResponse } from '@unipile/types';
import { isCancel } from 'axios';
import { LoaderIcon } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRequestCheckpoint } from '../../../features/accounts/api/request-checkpoint';
import { useSolveCheckpoint } from '../../../features/accounts/api/solve-checkpoint';
import { Button } from '../../shadcn/Button';
import { Field, FieldGroup } from '../../shadcn/Field';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../shadcn/InputOTP';
import { Spinner } from '../../shadcn/Spinner';
import { WizardStepContainer } from '../containers/WizardStepContainer';

/**
 * A checkpoint that allows both OTP code entry and in-app validation.
 * Combines the logic of CodeCheckpoint and ValidationCheckpoint.
 */
export function OtpOrInAppValidationCheckpoint({
  onSuccess,
  onError,
  intent_id,
  title,
  description,
  length = 6,
  tryAnotherWayButton,
}: {
  onSuccess: (response: ProviderAuthenticationIntentResponse) => void;
  onError: (error: unknown) => void;
  intent_id: TypeId<'account'>;
  title: string;
  description: string;
  length?: number;
  tryAnotherWayButton?: boolean;
}) {
  const validationAbortControllerRef = useRef<AbortController | null>(null);
  const getValidationSignal = useCallback(() => validationAbortControllerRef.current?.signal, []);
  const { t } = useTranslation();

  // Mutation for code submission
  const { mutate: submitCode, isPending: isSubmittingCode } = useSolveCheckpoint({
    mutationConfig: {
      onSuccess,
      onError,
    },
  });

  // Mutation for in-app validation (long polling)
  const { mutate: startValidation } = useSolveCheckpoint({
    mutationConfig: {
      onSuccess,
      onError: (error) => {
        // Try another way will cause the request to be cancelled, so we don't want to show an error in this case
        if (isCancel(error)) return;

        onError(error);
      },
    },
    getSignal: getValidationSignal,
  });

  const { mutate: mutateRequestCheckpoint, isPending: isRequestCheckpointPending } = useRequestCheckpoint({
    mutationConfig: {
      onSuccess,
      onError,
    },
  });

  const form = useForm<ProviderAuthenticationCheckpointInput>();

  // Start in-app validation polling when component mounts
  useEffect(() => {
    const controller = new AbortController();
    validationAbortControllerRef.current = controller;
    const timer = setTimeout(() => {
      startValidation({ data: { code: '', intent_id } });
    }, 0);

    return () => {
      clearTimeout(timer);
      controller.abort();
      if (validationAbortControllerRef.current === controller) {
        validationAbortControllerRef.current = null;
      }
    };
  }, [intent_id, startValidation]);

  const onSubmit = (data: ProviderAuthenticationCheckpointInput) => {
    // Abort validation polling before submitting OTP to prevent callbacks on unmounted component
    validationAbortControllerRef.current?.abort();
    submitCode({ data: { ...data, intent_id } });
  };

  return (
    <WizardStepContainer title={title} description={description}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col items-center">
        <FieldGroup>
          <Controller
            control={form.control}
            name="code"
            rules={{ required: true }}
            render={({ field }) => (
              <Field className="flex justify-center">
                <InputOTP maxLength={length} containerClassName="w-full justify-center" {...field}>
                  <InputOTPGroup className="w-full justify-center">
                    {Array.from({ length }).map((_, index) => (
                      <InputOTPSlot key={index} index={index} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </Field>
            )}
          />

          <Button className="mt-8 w-full" type="submit" disabled={isSubmittingCode}>
            {isSubmittingCode && <Spinner />}
            {t('Common.next')}
          </Button>
          {tryAnotherWayButton && (
            <Button
              onClick={() => {
                validationAbortControllerRef.current?.abort();
                validationAbortControllerRef.current = null;
                mutateRequestCheckpoint({ data: { intent_id } });
              }}
              className="w-full cursor-pointer"
              type="button"
              variant={'outline'}
              disabled={isRequestCheckpointPending}
            >
              {isRequestCheckpointPending ? <LoaderIcon className="animate-spin" size={20} /> : t('ProviderWizard.tryAnotherWay')}
            </Button>
          )}
        </FieldGroup>
      </form>
    </WizardStepContainer>
  );
}
